import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Button, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import Grid from '@mui/material/Grid';

import { creditRatingScalesMapping } from 'shared/mappings';
import { ratingConstants } from 'shared/constants/company';
import { portfolioTypes } from 'shared/constants/portfolioTypes';
import { endorsementCreditRating } from 'shared/constants/summaryTab';
import { BlurWrapper } from 'ui';
import { SummaryCard, HeadSquare, SummaryCardLineChart } from 'modules/common/SummaryCard';
import { generalInformationMock } from 'modules/common/SummaryCard/Request/generalInformationMock';
import summaryTabHistoryData from 'assets/mockedData/summaryTabHistoryData.json';
import { RatingStatus } from 'shared/types/ratingStatus';
import { RequestEsgButton } from 'modules/common';
import { useSelector } from 'react-redux';
import { shopProductsCategories } from 'modules/shop/shared/constants/shopProductsCategories';
import { getIndustryByName } from 'selectors/common';
import { RequestEsgScoreModal } from 'modules/common/Modals';
import { ShopRelatedProductsModal } from 'modules/shop/components/ShopRelatedProductsModal';
import { addToScoringToolService } from 'services/esg';
import { requestSentimentScore } from 'services/esg/fetchSentimentSummary';
import { ShopProductLines } from 'modules/shop/shared/constants/shopProductLines';

const data = {
  latestChange: {},
  generalInformation: {},
  levels: {},
  dates: {},
  documents: [],
  ratingScalesData: creditRatingScalesMapping(
    portfolioTypes.ESG_ANALYSIS,
    ratingConstants.entityReview,
  ),
  generalInformationList: generalInformationMock,
  endorsementList: endorsementCreditRating,
};

const useStyles = makeStyles((theme) => ({
  root: {
    height: '100%',
    whiteSpace: 'pre-line',
  },
  content: {
    flexDirection: 'column',
    alignContent: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    height: '100%',
    whiteSpace: 'pre-line',
  },
  requestButton: {
    marginTop: '20px',
  },
  unlockButton: {
    height: 50,
    margin: '0 auto',
    marginTop: 20,
    padding: '5px 30px',
    fontSize: '1rem',
  },
  contentRow: {
    textAlign: 'center',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  link: {
    textDecoration: 'underline',
    cursor: 'pointer',
  },
}));

const useHeadSquareStyles = makeStyles({
  score: {
    fontWeight: 500,
    fontSize: 60,
  },
  max: {
    fontSize: 24,
  },
  scoreWidgetMainColumn: {
    height: '100%',
  },
});

const SummaryCardRequestESG = ({
  type,
  scopeoneId,
  dataExists,
  // eslint-disable-next-line no-unused-vars
  isSubscribed,
  isAvailable,
  requestState,
  industry,
  countryCode,
  onRequested,
  onUnlocked,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const headSquareClasses = useHeadSquareStyles();
  const isUnlockEnabled = false;
  const esgContractData = useSelector(({ esgContract }) => esgContract);

  const [isRequestEsgModalOpen, setRequestEsgModalOpen] = useState(false);
  const [isRelatedProductsModalOpen, setRelatedProductsModalOpen] = useState(false);
  const [productLine, setProductLine] = useState(
    dataExists ? ShopProductLines.Review : ShopProductLines.Tools,
  );

  useEffect(() => {
    setProductLine(dataExists ? ShopProductLines.Review : ShopProductLines.Tools);
  }, [dataExists]);

  const [esgIds, setEsgIds] = useState([]);

  const industryObj = useSelector(getIndustryByName(industry));

  const [esgProductData, setEsgProductData] = useState();

  useEffect(() => {
    setEsgProductData({
      category: shopProductsCategories.esgAnalysis,
      productLine,
      countriesAndIndustries: [
        {
          country: countryCode,
          industries: industryObj ? [industryObj.name] : [],
        },
      ],
    });
  }, [industryObj, countryCode, productLine]);

  const openRequestEsgModal = async () => {
    const addedItems = await addToScoringToolService(scopeoneId);
    setEsgIds(addedItems.map((item) => item.esgId));
    setRequestEsgModalOpen(true);
  };

  const requestEsgSentimentScore = async () =>
    requestSentimentScore(scopeoneId).then(() => onRequested(true));

  const openRelatedProductsModal = () => {
    setRelatedProductsModalOpen(true);
  };

  const handleUnlockedButton = (unlocked) => {
    setRelatedProductsModalOpen(false);
    onUnlocked(unlocked);
  };

  const closeRequestEsgModal = (requested) => {
    setRequestEsgModalOpen(false);
    onRequested(requested);
  };

  const buildSentimentContent = () => {
    // /* No company in sentify */
    if (!dataExists) {
      return <Typography>{t('SENTIMENT.SCORE_NOT_AVAILABLE')}</Typography>;
    }

    /* No permission to display the score */
    if (!isSubscribed) {
      return (
        <Button
          color="primary"
          variant="contained"
          onClick={openRelatedProductsModal}
          className={classes.unlockButton}
        >
          {t('UNLOCK')}
        </Button>
      );
    }

    /* No score available, need to request */
    if (!isAvailable) {
      return (
        <>
          <Typography>{t('SENTIMENT.SCORE_NOT_AVAILABLE')}</Typography>
          <RequestEsgButton
            onRequestClick={requestEsgSentimentScore}
            alreadyRequested={requestState === RatingStatus.DONE}
            className={classes.requestButton}
          />
        </>
      );
    }

    /* Default value, should never be reached */
    return <></>;
  };

  const buildImpactContent = () => {
    if (!dataExists) {
      // score does not exist

      if (requestState === RatingStatus.UNABLE_TO_REQUEST) {
        return (
          <>
            <Typography>{t('ESG.ONLY_AVAILABLE_FOR_CORPORATES')}</Typography>
          </>
        );
      }

      if (requestState === RatingStatus.IN_PROGRESS) {
        // financial statement already provided - waiting for a score - requested
        return (
          <>
            <Typography>{t('ESG.SCORE_REQUESTING.SUMMARY_TAB_REQUEST')}</Typography>
            <RequestEsgButton alreadyRequested className={classes.requestButton} />
          </>
        );
      }

      if (
        !requestState ||
        [RatingStatus.TO_DO, RatingStatus.DONE_BY_SCOPE].includes(requestState)
      ) {
        // financial statement was not provided

        if (esgContractData && esgContractData.esgRequestPermission) {
          // user has permission to request
          return (
            <>
              <Typography>{t('ESG.SCORE_REQUESTING.SUMMARY_TAB_REQUESTED')}</Typography>
              <RequestEsgButton
                onRequestClick={openRequestEsgModal}
                className={classes.requestButton}
              />
            </>
          );
        }

        if (isUnlockEnabled) {
          // user cannot request, but can unlock scoring tool
          return (
            <>
              <Typography>{t('ESG.SCORE_REQUESTING.SUMMARY_TAB_UNLOCK_TOOLS')}</Typography>
              <Button
                color="primary"
                variant="contained"
                onClick={openRelatedProductsModal}
                className={classes.unlockButton}
              >
                {t('UNLOCK')}
              </Button>
            </>
          );
        }

        // There is no data to unlock
        return <Typography>{t('ESG.SCORE_REQUESTING.SCORE_NOT_AVAILABLE')}</Typography>;
      }
    } else if (!isAvailable) {
      // data exists but is not available
      return (
        <>
          <Button
            color="primary"
            variant="contained"
            onClick={openRelatedProductsModal}
            className={classes.unlockButton}
          >
            {t('UNLOCK')}
          </Button>
        </>
      );
    }

    return (
      // else: should never happen - data exists and isAvailable, so this component should not be visible
      <Typography>{t('ESG.SCORE_REQUESTING.SOMETHING_WENT_WRONG')}</Typography>
    );
  };

  return (
    <>
      <BlurWrapper
        content={
          <Grid container className={classes.content}>
            {type === 'impact' && buildImpactContent()}
            {type === 'sentiment' && buildSentimentContent()}
          </Grid>
        }
        active
      >
        <SummaryCard
          headSquare={
            <HeadSquare title={t('Short term')}>
              <span className={headSquareClasses.score}>7.3</span>
              <span className={headSquareClasses.max}>/10</span>
            </HeadSquare>
          }
          history={<SummaryCardLineChart history={summaryTabHistoryData} />}
          data={data}
        />
      </BlurWrapper>

      <RequestEsgScoreModal
        open={isRequestEsgModalOpen}
        onClose={closeRequestEsgModal}
        selectIds={esgIds}
      />
      {isRelatedProductsModalOpen && esgProductData ? (
        <ShopRelatedProductsModal
          data={
            type === 'impact'
              ? esgProductData
              : {
                  category: shopProductsCategories.esgAnalysis,
                  productLine: ShopProductLines.Events,
                }
          }
          open={isRelatedProductsModalOpen}
          onClose={() => handleUnlockedButton(false)}
          onConfirm={() => handleUnlockedButton(true)}
        />
      ) : null}
    </>
  );
};

SummaryCardRequestESG.defaultProps = {
  type: 'impact',
  dataExists: false,
  isSubscribed: true,
  isAvailable: false,
  requestState: RatingStatus.TO_DO,
};

SummaryCardRequestESG.propTypes = {
  type: PropTypes.oneOf(['impact', 'sentiment']),
  scopeoneId: PropTypes.string.isRequired,
  dataExists: PropTypes.bool,
  isSubscribed: PropTypes.bool,
  isAvailable: PropTypes.bool,
  requestState: PropTypes.string,
  industry: PropTypes.string.isRequired,
  countryCode: PropTypes.string.isRequired,
  onRequested: PropTypes.func.isRequired,
  onUnlocked: PropTypes.func.isRequired,
};

export default SummaryCardRequestESG;
